import * as React from 'react';

import { Tooltip, Typography } from '@mui/material';

import { InformationChip } from '@europrocurement/flexy-components';
import { checkIcon, warningIcon } from '@europrocurement/l2d-icons';

interface EligibilityStatusChipProps {
    hasEligibleCodes: boolean;
    eligibleCount: number;
    totalCount: number;
    displayMode?: 'compact' | 'full' | 'codesOnly' | 'facturesOnly';
}

/**
 * Composant atomique pour afficher un chip d'état d'éligibilité
 *
 * Ce composant affiche un chip coloré indiquant si une facture ou un ensemble
 * de factures ont des codes de rejet éligibles nécessitant une action du fournisseur.
 * Supporte plusieurs modes d'affichage selon le contexte d'utilisation.
 *
 * @param hasEligibleCodes - Indique si il y a au moins un code éligible
 * @param eligibleCount - Nombre de codes/factures éligibles
 * @param totalCount - Nombre total de codes/factures
 * @param displayMode - Mode d'affichage : 'compact' (ex: "1/2"), 'full' (ex: "1/2 codes éligibles"), 'codesOnly' (ex: "1/2 codes")
 */
const EligibilityStatusChip: React.FunctionComponent<EligibilityStatusChipProps> = function ({
    hasEligibleCodes,
    eligibleCount,
    totalCount,
    displayMode = 'full',
}) {
    const getLabel = () => {
        switch (displayMode) {
            case 'compact':
                return totalCount === 0 ? 'Aucun' : `${eligibleCount}/${totalCount}`;
            case 'codesOnly':
                return `${eligibleCount}/${totalCount} codes`;
            case 'facturesOnly':
                return `${eligibleCount}/${totalCount} factures`;
            case 'full':
            default: {
                if (hasEligibleCodes) {
                    return `${eligibleCount}/${totalCount} codes éligibles`;
                }
                if (totalCount === 0) {
                    return 'Aucun code';
                }
                return 'Aucun code éligible';
            }
        }
    };

    const getTooltip = React.useMemo(() => {
        if (displayMode === 'facturesOnly') {
            return hasEligibleCodes
                ? `${eligibleCount} facture(s) sur ${totalCount} nécessitent une action du fournisseur`
                : `Aucune des ${totalCount} factures ne nécessite d'action du fournisseur`;
        }

        return hasEligibleCodes
            ? `Cette facture a ${eligibleCount} code(s) de rejet sur ${totalCount} nécessitant une action du fournisseur`
            : `Cette facture n'a aucun code de rejet nécessitant une action du fournisseur`;
    }, [displayMode, eligibleCount, hasEligibleCodes, totalCount]);

    return (
        <Tooltip title={getTooltip}>
            <Typography>
                <InformationChip
                    icon={hasEligibleCodes ? checkIcon.props.icon : warningIcon.props.icon}
                    label={getLabel()}
                    color={hasEligibleCodes ? 'success' : 'warning'}
                />
            </Typography>
        </Tooltip>
    );
};

export default EligibilityStatusChip;
